import { SidebarNav } from "@design";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { useSignOut } from "@/auth/useSignOut";
import { useSidebarFolderLabels, useSidebarNavLabels } from "@/components/designLabels";
import { useAboutDialog } from "@/features/about/AboutProvider";
import { useCurrentUser, usePermissions } from "@/features/account/useCurrentUser";
import { useFeedbackDialog } from "@/features/feedback/FeedbackProvider";
import { useNotificationsPanel } from "@/features/notifications/NotificationsProvider";
import { unreadCountOptions } from "@/features/notifications/notificationCache";
import { NewSpaceModal } from "@/features/spaces/NewSpaceModal";
import { initialsOf } from "@/lib/initials";
import { DESKTOP_ONLY, useAvailableOn } from "@/responsive/surfaces";
import { useOpenCommandPalette } from "./commandPalette/CommandPaletteProvider";
import { Logo } from "./Logo";
import { renderSpacePopup } from "./SpacePopup";
import { useSidebarUsage } from "./usage/useSidebarUsage";
import { useFavoritesCollapsed } from "./useFavoritesCollapsed";
import { useSidebarFolders } from "./useSidebarFolders";
import { useSidebarSpaces } from "./useSidebarSpaces";
import { useSpaceMovedToast } from "./useSpaceMovedToast";

/**
 * The design-system SidebarNav wired to routing, the current user, permissions
 * and surfaces. In the mobile drawer, `onClose` replaces the collapse toggle.
 */
export function AppSidebar({ onClose }: Readonly<{ onClose?: () => void }>) {
  const navigate = useNavigate();
  const baseLabels = useSidebarNavLabels();
  const { t } = useTranslation("shell");
  const openPalette = useOpenCommandPalette();
  const { spaces, togglePin, reorderPins } = useSidebarSpaces();
  const spaceKeys = useMemo(() => spaces.filter((sp) => !sp.private).map((sp) => sp.key), [spaces]);
  const folders = useSidebarFolders(spaceKeys);
  const folderLabels = useSidebarFolderLabels();
  const [favoritesCollapsed, setFavoritesCollapsed] = useFavoritesCollapsed();
  const { pathname } = useLocation();
  const { accountName } = useAuth();
  const signOut = useSignOut();
  const me = useCurrentUser();
  const { isPlatformAdmin } = usePermissions();
  const [collapsed, setCollapsed] = useState(false);
  const [creatingSpace, setCreatingSpace] = useState(false);
  const buildAvailable = useAvailableOn(DESKTOP_ONLY);
  const isAt = (prefix: string) => pathname === prefix || pathname.startsWith(`${prefix}/`);
  const unread = useQuery(unreadCountOptions());
  const unreadCount = unread.data?.data.count ?? 0;
  // The bell's dot is visual only; put the count in its accessible name.
  const labels = unreadCount
    ? { ...baseLabels, notifications: t("sidebar.notificationsUnread", { count: unreadCount }) }
    : baseLabels;
  const notifications = useNotificationsPanel();
  const feedback = useFeedbackDialog();
  const about = useAboutDialog();
  const usage = useSidebarUsage();

  const onSpaceMoved = useSpaceMovedToast(spaces, folders.folders);

  // In the mobile drawer, close it and show the dropdown as a sheet instead.
  const openNotifications = (bell: HTMLElement) => {
    onClose?.();
    notifications.toggle(onClose ? null : bell);
  };

  return (
    <>
      <SidebarNav
        logo={<Logo />}
        labels={labels}
        collapsed={onClose ? false : collapsed}
        onToggleCollapse={onClose ?? (() => setCollapsed((c) => !c))}
        onSearch={openPalette}
        onNewChat={() => navigate("/chat")}
        homeActive={pathname === "/"}
        onOpenHome={() => navigate("/")}
        chatsActive={pathname === "/chats"}
        onOpenChats={() => navigate("/chats")}
        spaces={spaces}
        renderSpacePopup={renderSpacePopup}
        activeSpaceKey={/^\/spaces\/([^/]+)/.exec(pathname)?.[1]}
        onOpenSpace={(key) => navigate(`/spaces/${key}`)}
        onTogglePinSpace={togglePin}
        onNewSpace={() => setCreatingSpace(true)}
        onReorderFavorites={reorderPins}
        favoritesCollapsed={favoritesCollapsed}
        onToggleFavoritesCollapsed={setFavoritesCollapsed}
        allSpacesActive={pathname === "/spaces"}
        spaceFolders={
          folders.folders && {
            folders: folders.folders,
            handlers: { ...folders.handlers, onSpaceMoved },
            labels: folderLabels,
          }
        }
        // The drawer lists every space: "View all" opens a popover, which would sit under the drawer's modal <dialog>.
        looseSpacesLimit={onClose ? Number.POSITIVE_INFINITY : undefined}
        onOpenAllSpaces={() => navigate("/spaces")}
        buildActive={isAt("/build")}
        onOpenBuild={buildAvailable ? () => navigate("/build") : undefined}
        adminActive={isAt("/admin")}
        onOpenAdmin={isPlatformAdmin ? () => navigate("/admin") : undefined}
        hasUnreadNotifications={unreadCount > 0}
        onOpenNotifications={openNotifications}
        notificationsOpen={notifications.isOpen}
        onOpenHelp={() => navigate("/help")}
        // In the mobile drawer, close it first: the dialog would sit under the drawer's modal <dialog>.
        onOpenFeedback={() => {
          onClose?.();
          feedback.open();
        }}
        onOpenAbout={() => {
          onClose?.();
          about.open();
        }}
        userInitials={initialsOf(me.data?.user.name ?? accountName)}
        onOpenProfile={() => navigate("/settings")}
        onSignOut={() => {
          onClose?.();
          void signOut();
        }}
        // Not in the mobile drawer: it's a modal <dialog> (top layer), and the ring's popover portals to <body>
        // underneath it. Phones get the budget banners and Settings → Usage instead.
        usage={onClose ? undefined : usage}
      />
      {creatingSpace && <NewSpaceModal onClose={() => setCreatingSpace(false)} />}
    </>
  );
}
